import { useNavigate } from "#routers/hooks";
import { linkToEditScenario } from "#routes/scenarios/[scenarioId]/edit";
import { useTableSortingAndPagination } from "#src/components/Redux/reducers/tableStateReducer";
import { useStorageKey } from "#src/hooks/useStorageKey";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Button,
  DataTable,
  DataTablePanel,
  Dialog,
  ItemAction,
  useToast,
  type HeaderType,
} from "@validereinc/common-components";
import type { ReportingGroupType } from "@validereinc/domain";
import { ReportingGroupAdapter } from "@validereinc/domain";
import React, { useState } from "react";

export const ReportingGroupTablePanel = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [itemToDelete, setItemToDelete] = useState<ReportingGroupType>();
  const nameOfDeletedItem = itemToDelete
    ? `"${itemToDelete.name}"`
    : "scenario";

  const [tableStateStorageKey] = useStorageKey(
    ["table-state"],
    ["reporting-groups", "list"]
  );
  const [tableState, setTableState] = useTableSortingAndPagination(
    {
      sortBy: "priority",
      sortDirection: "desc",
    },
    undefined,
    tableStateStorageKey
  );

  const reportingGroupsParams: Parameters<
    typeof ReportingGroupAdapter.getList
  >[0] = {
    page: tableState.page,
    pageSize: tableState.pageSize,
    sortBy: tableState.sortBy,
    sortDirection: tableState.sortDirection,
  };

  const reportingGroups = useQuery({
    queryKey: ["reportingGroups", reportingGroupsParams],
    queryFn: () => ReportingGroupAdapter.getList(reportingGroupsParams),
  });

  const deleteReportingGroup = useMutation({
    mutationFn: (id: string) => ReportingGroupAdapter.deleteOne({ id }),
    onSuccess: async () => {
      toast.push({
        intent: "success",
        description: `Successfully deleted ${nameOfDeletedItem}.`,
      });
      await queryClient.invalidateQueries({ queryKey: ["reportingGroups"] });
    },
    onSettled: () => {
      setItemToDelete(undefined);
    },
  });

  const headers: Array<HeaderType<ReportingGroupType>> = [
    {
      key: "name",
      label: "Name",
      isSortable: true,
    },
    {
      key: "description",
      label: "Description",
      renderComponent: ({ item }) => (
        <DataTable.DataRow.TextCell value={item.description} />
      ),
    },
  ];

  const getItemActions = ({
    item,
  }: {
    item: ReportingGroupType;
  }): ItemAction[] => [
    {
      label: "Edit",
      buttonProps: {
        onClick: () => navigate({ pathname: linkToEditScenario(item.id) }),
        icon: "pencil-simple",
      },
    },
    {
      label: "Delete",
      buttonProps: {
        onClick: () => setItemToDelete(item),
        icon: "trash",
      },
    },
  ];

  return (
    <>
      <DataTablePanel
        panelProps={{ title: "Scenarios" }}
        dataTableProps={{
          items: reportingGroups.data?.data ?? [],
          getItemId: (item) => item.id,
          pagination: {
            page: tableState.page,
            pageSize: tableState.pageSize,
            total: reportingGroups.data?.total_entries,
          },
          sorting: {
            sortBy: tableState.sortBy,
            sortDirection: tableState.sortDirection,
          },
          onSortChange: setTableState,
          onPaginationChange: setTableState,
          intent: "simplicity-first",
          isLoading: reportingGroups.isLoading,
          isBusy: reportingGroups.isFetching,
          headers,
          getItemActions,
          showRowNumbers: true,
        }}
      />
      <Dialog
        isOpen={!!itemToDelete}
        onClose={() => setItemToDelete(undefined)}
        title={`Delete ${nameOfDeletedItem}`}
        actionRow={[
          <Button
            key="delete-dialog-action"
            onClick={() => deleteReportingGroup.mutate(itemToDelete?.id ?? "")}
            variant="error"
          >
            Delete
          </Button>,
        ]}
      >
        Are you sure you want to delete this reporting group? This action cannot
        be undone.
      </Dialog>
    </>
  );
};
